import {
  Box,
  BoxProps,
  ButtonNew as Button,
  ColorOptions,
  convertHexToRGBA,
  RadioButton,
  RadioButtonProps,
  Spacer,
  Text,
  TextProps
} from '@jfs/ui-new'
import { useCallback } from 'react'
import { AccordionList } from '../../../../comps/AccordionList'
import TOneFooter from '../../../../comps/TOneFooter'
import { MerchantTheme } from '../../../../data/models/Config'
import { formatAmount } from '../../../../utils'
import { EMI_TYPES } from '../../../../utils/constants'

interface EmiListProps {
  theme: any
  emiPlansData: any
  amountPayable: any
  onPlanSelected: any
}

export const EmiList = ({ theme, emiPlansData, amountPayable, onPlanSelected }: EmiListProps) => {
  const style = stylesFunc(theme)
  const _renderHeader = ({ item, index, onHeaderClick }: any) => {
    return (
      <Box {...style.listItemHeading}>
        <RadioButton checked={item.isExpanded} {...style.radioButton} onChange={() => onHeaderClick(index)} />
        <Box flexDirection={'column'} ml={18}>
          <Box flexDirection={'row'}>
            <Text {...style.headerText}>{item.header.title}</Text>
            {item.header.isNoCostEmi && <Text {...style.greenText}>No Cost EMI</Text>}
          </Box>
          <Text {...style.headerDesciptionText}>{item.header.subTitle}</Text>
        </Box>
      </Box>
    )
  }

  const _renderData = ({ item }: any) => {
    return (
      <>
        <Box {...style.listEMIData}>
          <Text {...style.costBreakUp}>Cost Breakup</Text>
          <Spacer size={16} />
          <Box {...style.modalDataContainer}>
            <Text {...style.emiBreakupTitle}>Gross Amount</Text>
            <Text {...style.emiBreakupValue}>{`₹${formatAmount(amountPayable + '')}`}</Text>
          </Box>
          <Spacer size={12} />
          <Box {...style.modalDataContainer}>
            <Text {...style.emiBreakupTitle}>{`Interest Payable(${item?.interestRate}%)`}</Text>
            <Text {...style.emiBreakupValue}>{`₹${formatAmount(item?.interest)}`}</Text>
          </Box>
          <Spacer size={12} />
          <Box {...style.modalDataContainer}>
            <Text {...style.emiBreakupTitle}>
              {item?.processingFeePercent ? `Processing fee(${item?.processingFeePercent}%)` : 'Processing fee'}
            </Text>
            <Text {...style.emiBreakupValue}>{`₹${formatAmount(item?.processingFee)}`}</Text>
          </Box>
          <Spacer size={12} />

          <Box {...style.modalDataContainer}>
            <Text {...style.emiBreakupTitle}>
              {item?.convenienceFeePercent ? `Convenience fee(${item?.convenienceFeePercent}%)` : 'Convenience fee'}
            </Text>
            <Text {...style.emiBreakupValue}>{`₹${formatAmount(item?.convenienceFee)}`}</Text>
          </Box>
          {Number(item?.discount) > 0 && (
            <>
              <Spacer size={20} />
              <Box {...style.modalDataContainer}>
                <Text {...style.emiBreakupTitle}>Discount</Text>
                <Text {...style.emiBreakupValue}>{`₹${formatAmount(item?.discount)}`}</Text>
              </Box>
            </>
          )}
          <Spacer size={20} />
          <Box {...style.separatorStyle} />
          <Spacer size={20} />
          <Box {...style.modalDataContainer}>
            <Text {...style.totalPayable}>Total Payable</Text>
            <Text {...style.totalPayableAmount}>{`₹${formatAmount(item?.totalAmount as any)}`}</Text>
          </Box>
          <Spacer size={16} />
          <Text {...style.emiBreakupTitle}>
            Full amount of <b> ₹{formatAmount(item?.totalAmount)}</b> will be blocked from your account, which will be
            converted into EMI by your bank in 3-4 days.
          </Text>
          <Spacer size={20} />
          <Button
            width={'100%'}
            height={48}
            title="Select Plan"
            testID="select-plan"
            onPress={() => {
              onPlanSelected(item)
            }}
            color={theme?.brandColor as ColorOptions}
            titleColor={theme?.headingText as ColorOptions}
            hoverColor={theme?.brandColor as ColorOptions}
            underlayColor={theme?.brandColor as ColorOptions}
            loadingColor={theme?.headingText as ColorOptions}
          />
          <Spacer size={16} />
          <TOneFooter />
        </Box>
        <Box {...style.separatorStyle} />
      </>
    )
  }

  const getFormatedDataForAccordian = useCallback(() => {
    return emiPlansData.emiPlans.map((emi: any) => ({
      header: {
        title:
          emi.schemeType === EMI_TYPES.NO_COST
            ? `Pay ₹${formatAmount(emi.value)} for ${emi?.tenure?.replace(/\D/g, '')} Months | `
            : `Pay ₹${formatAmount(emi.value)} for ${emi?.tenure?.replace(/\D/g, '')} Months @${emi?.interestRate
            }% p.a`,
        subTitle: `Payable: ₹${formatAmount(emi.totalAmount)}`,
        isNoCostEmi: emi.schemeType === EMI_TYPES.NO_COST,
      },
      data: [emi],
    }))
  }, [emiPlansData.emiPlans])

  return (
    <Box overflowY={'scroll'} height={'100%'}>
      <AccordionList
        theme={theme}
        data={getFormatedDataForAccordian()}
        renderHeader={_renderHeader}
        renderData={_renderData}
      />
    </Box>
  )
}

const stylesFunc = (theme: typeof MerchantTheme.properties) => ({
  headerText: {
    fontSize: 12,
    color: theme.bodyTextColor as any,
    fontFamily: 'jioType-medium',
    lineHeight: 18,
  } as TextProps,
  greenText: {
    fontSize: 12,
    color: 'AVAILABLE_GREEN',
    fontFamily: 'jioType-medium',
    lineHeight: 18,
  } as TextProps,
  headerDesciptionText: {
    fontSize: 12,
    color: theme.bodyTextColor as any,
    fontFamily: 'jioType-light',
    lineHeight: 18,
  } as TextProps,
  listItemHeading: {
    borderBottomWidth: 0.5,
    borderBottomColor: convertHexToRGBA(theme.bodyTextColor, 10),
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
  } as BoxProps,
  radioButton: {
    size: 16,
    color: theme?.brandColor as any,
    hoverColor: 'TRANSPARENT',
    unCheckedColor: convertHexToRGBA(theme?.bodyTextColor, 30),
  } as RadioButtonProps,
  separatorStyle: {
    height: 0.5,
    backgroundColor: convertHexToRGBA(theme?.bodyTextColor, 10) as ColorOptions,
  } as BoxProps,
  listEMIData: {
    padding: 24,
    backgroundColor: convertHexToRGBA(theme?.bodyTextColor, 2) as any,
  } as BoxProps,
  costBreakUp: {
    fontSize: 12,
    color: theme?.bodyTextColor as any,
    fontFamily: 'jioType-bold',
  } as TextProps,
  modalDataContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  } as BoxProps,
  emiBreakupTitle: {
    fontSize: 12,
    color: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
    fontFamily: 'jioType-light',
  } as TextProps,
  emiBreakupValue: {
    fontSize: 12,
    color: theme?.bodyTextColor as any,
    fontFamily: 'jioType-bold',
  } as TextProps,
  totalPayable: {
    fontSize: 14,
    color: theme?.bodyTextColor as any,
    fontFamily: 'jioType-medium',
  } as TextProps,
  totalPayableAmount: {
    fontSize: 14,
    color: theme?.brandColor as any,
    fontFamily: 'jioType-bold',
  } as TextProps,
  header: {
    fontSize: 14,
    color: theme?.bodyTextColor as any,
    fontFamily: 'jiotype-Medium',
  } as TextProps,
})
